const verdictColors = {
    'Pass': 'bg-green-100 text-green-800',
    'Fail': 'bg-red-100 text-red-800',
};

const VerdictBadge = ({ verdict }) => {
    if (!verdict) return null;
    const color = verdictColors[verdict] || 'bg-gray-100 text-gray-700';
    return <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full ${color}`}>{verdict}</span>;
};

const fmt = (v) => {
    if (v === null || v === undefined) return '—';
    return typeof v === 'number' ? v.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v;
};

const pct = (v) => {
    if (v === null || v === undefined || v === 0) return '—';
    return `${(v * 100).toFixed(1)}%`;
};

const YesNoBadge = ({ value }) => {
    if (!value) return <span className="text-xs text-gray-400">—</span>;
    const color = value === 'Yes' ? 'text-green-700' : 'text-red-600';
    return <span className={`text-sm font-medium ${color}`}>{value}</span>;
};

function CollateralSecuritySection({ collateralSecurity, onChange, readOnly }) {
    const cs = collateralSecurity || {};

    const pledgeChecks = [
        { key: 'signedPledgeFormReceived', label: 'Signed pledge form received' },
        { key: 'customerSignaturePresent', label: 'Customer signature present' },
        { key: 'trusteeSignaturePresent', label: 'Trustee signature present' },
        { key: 'formForwarded', label: 'Form forwarded to trustee' },
        { key: 'lienOnFutureContributions', label: 'Lien on future contributions' },
        { key: 'standingOrderSetUp', label: 'Standing order set up' },
        { key: 'confirmedContributionHistory', label: 'Confirmed contribution history' },
    ];

    const handleChange = (field, value) => {
        if (onChange) onChange('collateralSecurity', field, value);
    };

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-y border-gray-200 rounded-t-lg">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Collateral & Security</h3>
                <VerdictBadge verdict={cs.scrVerdict} />
            </div>

            {/* Collateral Details */}
            <div className="px-4 pt-3 pb-1">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tier 3 / Pension Details</span>
            </div>
            <div className="grid grid-cols-2 gap-3 px-4 py-3">
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Pension Trustee</label>
                    <input type="text" value={cs.pensionTrustee || ''} disabled={readOnly}
                        onChange={e => handleChange('pensionTrustee', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-1 focus:ring-[#1e3a6e] focus:border-[#1e3a6e] disabled:bg-gray-50" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Pension ID</label>
                    <input type="text" value={cs.pensionId || ''} disabled={readOnly}
                        onChange={e => handleChange('pensionId', e.target.value)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-1 focus:ring-[#1e3a6e] focus:border-[#1e3a6e] disabled:bg-gray-50" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Current Balance (GHS)</label>
                    <input type="number" value={cs.currentBalance || ''} disabled={readOnly}
                        onChange={e => handleChange('currentBalance', parseFloat(e.target.value) || 0)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-1 focus:ring-[#1e3a6e] focus:border-[#1e3a6e] disabled:bg-gray-50" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Avg Monthly Contribution (GHS)</label>
                    <input type="number" value={cs.avgMonthlyContribution || ''} disabled={readOnly}
                        onChange={e => handleChange('avgMonthlyContribution', parseFloat(e.target.value) || 0)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-1 focus:ring-[#1e3a6e] focus:border-[#1e3a6e] disabled:bg-gray-50" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Contribution History (Months)</label>
                    <input type="number" value={cs.contributionHistoryMonths || ''} disabled={readOnly}
                        onChange={e => handleChange('contributionHistoryMonths', parseInt(e.target.value) || 0)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-1 focus:ring-[#1e3a6e] focus:border-[#1e3a6e] disabled:bg-gray-50" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Contributions Last 12 Months</label>
                    <input type="number" value={cs.contributionsLast12Months || ''} disabled={readOnly}
                        onChange={e => handleChange('contributionsLast12Months', parseInt(e.target.value) || 0)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-1 focus:ring-[#1e3a6e] focus:border-[#1e3a6e] disabled:bg-gray-50" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Pledged Future Months</label>
                    <input type="number" value={cs.pledgedFutureMonths || ''} disabled={readOnly}
                        onChange={e => handleChange('pledgedFutureMonths', parseInt(e.target.value) || 0)}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-1 focus:ring-[#1e3a6e] focus:border-[#1e3a6e] disabled:bg-gray-50" />
                </div>
            </div>

            {/* Future-contribution Pledge Checklist */}
            <div className="px-4 pt-3 pb-1 border-t border-gray-200">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Future-contribution Pledge Checklist</span>
            </div>
            <div className="divide-y divide-gray-100">
                {pledgeChecks.map(check => (
                    <div key={check.key} className="flex items-center justify-between px-4 py-2.5">
                        <span className="text-sm text-gray-600">{check.label}</span>
                        {readOnly ? (
                            <YesNoBadge value={cs[check.key]} />
                        ) : (
                            <select value={cs[check.key] || ''} onChange={e => handleChange(check.key, e.target.value)}
                                className="rounded-lg border border-gray-200 px-2 py-1 text-sm focus:ring-1 focus:ring-[#1e3a6e]">
                                <option value="">—</option>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                            </select>
                        )}
                    </div>
                ))}
            </div>

            {/* SCR & Eligibility */}
            <div className="px-4 pt-4 pb-1 border-t border-gray-200">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">SCR & Eligibility</span>
            </div>
            <div className="divide-y divide-gray-100">
                <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-gray-600">Projected future contributions</span>
                    <span className="text-sm font-medium text-gray-900">GHS {fmt(cs.projectedFutureContributions)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-gray-600">Total pledged security</span>
                    <span className="text-sm font-medium text-gray-900">GHS {fmt(cs.totalPledgedSecurity)}</span>
                </div>
                <div className={`flex items-center justify-between px-4 py-3 ${cs.scr < 0.3 ? 'bg-red-50' : cs.scr > 0 ? 'bg-green-50' : ''}`}>
                    <span className="text-sm font-medium text-gray-700">Security Coverage Ratio (SCR)</span>
                    <div className="flex items-center gap-2">
                        <span className={`text-sm font-bold ${cs.scr < 0.3 ? 'text-red-700' : 'text-green-700'}`}>
                            {pct(cs.scr)}
                        </span>
                        <VerdictBadge verdict={cs.scrVerdict} />
                    </div>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-gray-600">Eligible by collateral (80% of pledged)</span>
                    <span className="text-sm font-medium text-gray-900">GHS {fmt(cs.eligibleByCollateral)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-sm text-gray-600">Eligible by income (60% DTI flat)</span>
                    <span className="text-sm font-medium text-gray-900">GHS {fmt(cs.eligibleByIncome)}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-3 bg-blue-50/50">
                    <span className="text-sm font-semibold text-gray-700">Final eligible amount</span>
                    <span className="text-sm font-bold text-[#1e3a6e]">GHS {fmt(cs.finalEligibleAmount)}</span>
                </div>
            </div>
        </div>
    );
}

export default CollateralSecuritySection;
